// src/components/TopBar.jsx
import React, { useState } from 'react';
import { LogOut, Home, Plus, ChevronDown, Check, X, Menu, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from './LanguageSelector';
import { t } from '../lib/i18n';

const ACCOUNT_TYPES = ['Live', 'Prop Firm', 'Challenge', 'Practice'];
const ODF_USER_ID = 'ff2986f5-583c-4164-9980-08dc47b5a621';

const typeColor = (type) => {
  if (type === 'Live')       return '#00e5a0';
  if (type === 'Prop Firm')  return '#4cc9f0';
  if (type === 'Challenge')  return '#ffd166';
  return '#8888a8';
};

export default function TopBar({ accounts, activeAccount, activeAccountId, setActiveAccountId, createAccount, deleteAccount, onHome, onTrackRecord, lang, setLang }) {
  const { user, signOut } = useAuth();
  const isODF = user?.id === ODF_USER_ID;
  const [dropOpen, setDropOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState({ name:'', type:'Live', broker:'', startingBalance:'' });
  const [saving,   setSaving]   = useState(false);

  const handleCreate = async () => {
    if (!form.name) return;
    setSaving(true);
    await createAccount({
      name:            form.name,
      type:            form.type,
      broker:          form.broker,
      startingBalance: parseFloat(form.startingBalance) || 10000,
    });
    setForm({ name:'', type:'Live', broker:'', startingBalance:'' });
    setShowForm(false); setSaving(false); setDropOpen(false);
  };

  const inpStyle = { background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#f0f0f5', padding:'8px 10px', fontSize:12, outline:'none', fontFamily:"'IBM Plex Sans',sans-serif", width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ height:54, background:'#111118', borderBottom:'1px solid #1e1e2e', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 20px', flexShrink:0, position:'relative', zIndex:20 }}>

      {/* Left */}
      <button onClick={onHome} style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'color 0.2s', padding:0, flexShrink:0 }}
        onMouseEnter={e=>e.currentTarget.style.color='#f0f0f5'}
        onMouseLeave={e=>e.currentTarget.style.color='#55556a'}>
        <Home size={14} />
        <span className="topbar-home-label">Home</span>
      </button>

      {isODF && onTrackRecord && (
        <button onClick={onTrackRecord} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', color:'#55556a', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'color 0.2s', padding:'0 8px', flexShrink:0 }}
          onMouseEnter={e=>e.currentTarget.style.color='#00e5a0'}
          onMouseLeave={e=>e.currentTarget.style.color='#55556a'}>
          <BarChart3 size={13} />
          <span className="topbar-home-label">ODF's Dashboard</span>
        </button>
      )}

      {/* Center — Account switcher */}
      <div style={{ position:'relative' }}>
        <button onClick={() => setDropOpen(o=>!o)} style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.04)', border:'1px solid #1e1e2e', borderRadius:8, padding:'6px 12px', cursor:'pointer', outline:'none', transition:'border-color 0.2s', maxWidth: 220 }}
          onMouseEnter={e=>e.currentTarget.style.borderColor='#2a2a3e'}
          onMouseLeave={e=>e.currentTarget.style.borderColor='#1e1e2e'}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:typeColor(activeAccount?.type), flexShrink:0 }} />
          <span style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120 }}>
            {activeAccount?.name || 'Select Account'}
          </span>
          <span className="topbar-acct-type" style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
            {activeAccount?.type}
          </span>
          <ChevronDown size={13} style={{ color:'#55556a', transform:dropOpen?'rotate(180deg)':'rotate(0)', transition:'transform 0.2s', flexShrink:0 }} />
        </button>

        {dropOpen && (
          <div style={{ position:'absolute', top:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)', background:'#16161f', border:'1px solid #2a2a3e', borderRadius:10, minWidth:260, zIndex:100, boxShadow:'0 8px 32px rgba(0,0,0,0.5)', animation:'fadeIn 0.15s ease', overflow:'hidden' }}>
            {(accounts||[]).map(acc => (
              <div key={acc.id} onClick={() => { setActiveAccountId(acc.id); setDropOpen(false); }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', cursor:'pointer', background:activeAccountId===acc.id?'rgba(0,229,160,0.06)':'transparent', borderBottom:'1px solid #1a1a26', transition:'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background=activeAccountId===acc.id?'rgba(0,229,160,0.08)':'#1c1c28'}
                onMouseLeave={e=>e.currentTarget.style.background=activeAccountId===acc.id?'rgba(0,229,160,0.06)':'transparent'}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:typeColor(acc.type), flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:'#f0f0f5', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{acc.name}</div>
                  <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
                    {acc.type}{acc.broker?` · ${acc.broker}`:''}{acc.starting_balance?` · $${Number(acc.starting_balance).toLocaleString()}`:''}
                  </div>
                </div>
                {activeAccountId===acc.id && <Check size={13} style={{ color:'#00e5a0', flexShrink:0 }} />}
                {(accounts||[]).length > 1 && (
                  <button onClick={e => { e.stopPropagation(); deleteAccount(acc.id); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:2, display:'flex', flexShrink:0, transition:'color 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.color='#ff4d6d'}
                    onMouseLeave={e=>e.currentTarget.style.color='#55556a'}>
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}

            {!showForm ? (
              <button onClick={() => setShowForm(true)} style={{ width:'100%', padding:'11px 14px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8, color:'#00e5a0', fontSize:13, fontFamily:"'IBM Plex Sans',sans-serif", transition:'background 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,229,160,0.05)'}
                onMouseLeave={e=>e.currentTarget.style.background='none'}>
                <Plus size={14} /> Add Account
              </button>
            ) : (
              <div style={{ padding:14, borderTop:'1px solid #1e1e2e' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <input placeholder="Account name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={inpStyle} />
                  <div style={{ display:'flex', gap:6 }}>
                    <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} style={{ ...inpStyle, width:'auto', flex:1 }}>
                      {ACCOUNT_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                    <input placeholder="Broker" value={form.broker} onChange={e=>setForm(f=>({...f,broker:e.target.value}))} style={{ ...inpStyle, flex:1 }} />
                  </div>
                  <input placeholder="Starting balance ($)" type="number" value={form.startingBalance} onChange={e=>setForm(f=>({...f,startingBalance:e.target.value}))} style={inpStyle} />
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={handleCreate} disabled={saving} style={{ flex:2, background:'#00e5a0', color:'#0a0a0f', border:'none', borderRadius:7, padding:'8px', fontSize:12, fontWeight:600, cursor:saving?'not-allowed':'pointer', fontFamily:"'IBM Plex Sans',sans-serif", opacity:saving?0.7:1 }}>
                      {saving ? 'Saving...' : 'Create Account'}
                    </button>
                    <button onClick={() => { setShowForm(false); setForm({ name:'', type:'Live', broker:'', startingBalance:'' }); }} style={{ flex:1, background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:7, padding:'8px', fontSize:12, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
        <div className="topbar-lang" title="Language applies to the home/landing page"><LanguageSelector lang={lang} setLang={setLang} /></div>
        <span className="topbar-email" style={{ fontSize:11, color:'#2a2a3e', fontFamily:"'DM Mono',monospace", maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {user?.email}
        </span>
        <button onClick={signOut} style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'1px solid #1e1e2e', borderRadius:7, padding:'6px 10px', cursor:'pointer', color:'#8888a8', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s', flexShrink:0 }}
          onMouseEnter={e=>{ e.currentTarget.style.color='#ff4d6d'; e.currentTarget.style.borderColor='rgba(255,77,109,0.3)'; }}
          onMouseLeave={e=>{ e.currentTarget.style.color='#8888a8'; e.currentTarget.style.borderColor='#1e1e2e'; }}>
          <LogOut size={13} />
          <span className="topbar-signout-label">{t(lang,'signOut')}</span>
        </button>
      </div>
    </div>
  );
}
